import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  HelpCircle, Loader2, ChevronRight, ArrowLeft, BookOpen, Plus,
  Edit2, Trash2, Rocket, Target, Search, CheckSquare, Sparkles,
  MessageSquare, Users, CreditCard, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Rocket, Target, Search, CheckSquare, Sparkles,
  MessageSquare, Users, CreditCard, Shield, BookOpen, HelpCircle
};

export default function HelpDeskPage({ user }) {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);

  // Admin state
  const isAdmin = user?.system_role === "platform_owner";
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(null); // "article" | "category"
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/help/categories`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (e) {
      toast.error("Failed to load help content");
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async (cat) => {
    setSelectedCategory(cat);
    setSelectedArticle(null);
    setLoadingArticles(true);
    try {
      const res = await fetch(`${API}/help/categories/${cat.category_id}/articles`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
      }
    } catch (e) {
      toast.error("Failed to load articles");
    } finally {
      setLoadingArticles(false);
    }
  };

  const openArticle = async (article) => {
    setSelectedArticle(article);
  };

  const goBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
      setArticles([]);
    }
  };

  // Admin: Edit/Create article
  const openArticleEditor = (article = null) => {
    setEditMode("article");
    setEditForm(article ? { ...article } : {
      title: "", content: "", category_id: selectedCategory?.category_id || "", is_published: true
    });
    setEditDialogOpen(true);
  };

  const openCategoryEditor = (cat = null) => {
    setEditMode("category");
    setEditForm(cat ? { ...cat } : { name: "", description: "", icon: "BookOpen" });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let url, method;
      if (editMode === "article") {
        if (!editForm.title?.trim()) { toast.error("Title required"); setSaving(false); return; }
        url = editForm.article_id
          ? `${API}/help/articles/${editForm.article_id}`
          : `${API}/help/articles`;
        method = editForm.article_id ? "PUT" : "POST";
      } else {
        if (!editForm.name?.trim()) { toast.error("Name required"); setSaving(false); return; }
        url = editForm.category_id
          ? `${API}/help/categories/${editForm.category_id}`
          : `${API}/help/categories`;
        method = editForm.category_id ? "PUT" : "POST";
      }

      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(editForm)
      });

      if (res.ok) {
        toast.success("Saved successfully");
        setEditDialogOpen(false);
        fetchCategories();
        if (selectedCategory) fetchArticles(selectedCategory);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await fetch(`${API}/help/articles/${articleId}`, { method: "DELETE", credentials: "include" });
      toast.success("Article deleted");
      if (selectedArticle?.article_id === articleId) setSelectedArticle(null);
      if (selectedCategory) fetchArticles(selectedCategory);
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category and all its articles?")) return;
    try {
      await fetch(`${API}/help/categories/${categoryId}`, { method: "DELETE", credentials: "include" });
      toast.success("Category deleted");
      if (selectedCategory?.category_id === categoryId) {
        setSelectedCategory(null);
        setArticles([]);
      }
      fetchCategories();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Article view
  if (selectedArticle) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="help-article-view">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={goBack} data-testid="help-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {selectedCategory?.name}
          </Button>
          {isAdmin && (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => openArticleEditor(selectedArticle)} data-testid="edit-article-btn">
                <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteArticle(selectedArticle.article_id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
        <Card>
          <CardHeader>
            <Badge variant="outline" className="w-fit text-xs mb-2">{selectedCategory?.name}</Badge>
            <CardTitle className="text-xl">{selectedArticle.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm sm:prose lg:prose-lg prose-slate max-w-none" data-testid="article-content">
              <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Articles list for a category
  if (selectedCategory) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="help-articles-list">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={goBack} data-testid="help-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> All Topics
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => openArticleEditor()} data-testid="add-article-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Article
            </Button>
          )}
        </div>

        <div>
          <h2 className="text-xl font-bold text-slate-800">{selectedCategory.name}</h2>
          <p className="text-slate-500 text-sm mt-1">{selectedCategory.description}</p>
        </div>

        {loadingArticles ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : articles.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No articles in this category yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {articles.map((art) => (
              <Card
                key={art.article_id}
                className="cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all group"
                onClick={() => openArticle(art)}
                data-testid={`article-${art.article_id}`}
              >
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-primary/60 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">{art.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Updated {new Date(art.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); openArticleEditor(art); }}>
                        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Admin edit dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode === "article"
                  ? (editForm.article_id ? "Edit Article" : "New Article")
                  : (editForm.category_id ? "Edit Category" : "New Category")}
              </DialogTitle>
            </DialogHeader>
            {editMode === "article" ? (
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="article-title-input" />
                </div>
                <div>
                  <Label>Content (Markdown)</Label>
                  <Textarea value={editForm.content || ""} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} rows={15} className="font-mono text-sm" data-testid="article-content-input" />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                </div>
                <div>
                  <Label>Icon</Label>
                  <Select value={editForm.icon || "BookOpen"} onValueChange={(v) => setEditForm({ ...editForm, icon: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(ICON_MAP).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleSave} disabled={saving} data-testid="save-help-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Category grid (home view)
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="help-desk-page">
      <div className="text-center">
        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Help Center</h1>
        <p className="text-slate-500 mt-1">Learn how to use Sales CRM Coach</p>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => openCategoryEditor()} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Category
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => {
          const IconComp = ICON_MAP[cat.icon] || BookOpen;
          return (
            <Card
              key={cat.category_id}
              className="cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
              onClick={() => fetchArticles(cat)}
              data-testid={`help-category-${cat.category_id}`}
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                    <IconComp className="w-5 h-5 text-primary" />
                  </div>
                  {isAdmin && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openCategoryEditor(cat); }}>
                        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.category_id); }}>
                        <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-slate-800">{cat.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{cat.description}</p>
                <Badge variant="secondary" className="text-xs mt-3">
                  {cat.article_count} article{cat.article_count !== 1 ? "s" : ""}
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Admin dialogs */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editForm.category_id ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={editForm.icon || "BookOpen"} onValueChange={(v) => setEditForm({ ...editForm, icon: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(ICON_MAP).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} disabled={saving} data-testid="save-help-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
